import {
  rtkApi,
  GET_TEACHER_ASSIGNMENTS,
  GET_TEACHER_ASSIGNMENTS_SUMMARY,
  GET_TEACHER_ASSIGNMENTS_DETAILS,
  GET_TEACHER_ASSIGNMENTS_COURSES,
  GET_TEACHER_LESSON_BASE,
  GET_TEACHER_LESSON_STUDENTS,
  GET_TEACHER_LESSON_SUBMISSION,
} from '@/shared/api';
import { SuccessApiResponse } from '@/shared/types/common';
import { createFormData } from '@/shared/lib/utils/createFormData';
import {
  ITeacherAssignmentsResponse,
  ITeacherAssignmentsSummaryRequest,
  ITeacherAssignmentsSummaryResponse,
  ITeacherAssignmentsDetailsRequest,
  ITeacherAssignmentsDetailsResponse,
  ITeacherAssignmentsCoursesResponse,
  ITeacherLessonBaseRequest,
  ITeacherLessonBaseResponse,
  ITeacherLessonStudentsRequest,
  ITeacherLessonStudentsResponse,
  ITeacherLessonSubmissionRequest,
  ITeacherLessonSubmissionResponse,
  ITeacherAssessmentRequest,
  ITeacherAssessmentResponse,
  ITeacherAssessmentWithFeedbackRequest,
  ITeacherAssessmentWithFeedbackResponse,
} from './types';

const teacherAssignmentApi = rtkApi.injectEndpoints({
  endpoints: (build) => ({
    // GET /teacher/assignments
    getTeacherAssignments: build.query<ITeacherAssignmentsResponse, void>({
      query: () => '/teacher/assignments',
      transformResponse: (response: SuccessApiResponse<ITeacherAssignmentsResponse>) =>
        response.data,
      providesTags: [GET_TEACHER_ASSIGNMENTS],
    }),

    // GET /teacher/assignments/summary
    getTeacherAssignmentsSummary: build.query<
      ITeacherAssignmentsSummaryResponse,
      ITeacherAssignmentsSummaryRequest
    >({
      query: (params) => ({
        url: '/teacher/assignments/summary',
        params,
      }),
      transformResponse: (response: SuccessApiResponse<ITeacherAssignmentsSummaryResponse>) =>
        response.data,
      providesTags: [GET_TEACHER_ASSIGNMENTS_SUMMARY],
    }),

    // GET /teacher/assignments/details
    getTeacherAssignmentsDetails: build.query<
      ITeacherAssignmentsDetailsResponse,
      ITeacherAssignmentsDetailsRequest
    >({
      query: (params) => ({
        url: '/teacher/assignments/details',
        params,
      }),
      transformResponse: (response: SuccessApiResponse<ITeacherAssignmentsDetailsResponse>) =>
        response.data,
      providesTags: [GET_TEACHER_ASSIGNMENTS_DETAILS],
    }),

    // GET /teacher/assignments/courses
    getTeacherAssignmentsCourses: build.query<ITeacherAssignmentsCoursesResponse, void>({
      query: () => '/teacher/assignments/courses',
      transformResponse: (response: SuccessApiResponse<ITeacherAssignmentsCoursesResponse>) =>
        response.data,
      providesTags: [GET_TEACHER_ASSIGNMENTS_COURSES],
    }),

    // GET /teacher/assignments/lesson-base
    getTeacherLessonBase: build.query<ITeacherLessonBaseResponse, ITeacherLessonBaseRequest>({
      query: (params) => ({
        url: '/teacher/assignments/lesson-base',
        params,
      }),
      transformResponse: (response: SuccessApiResponse<ITeacherLessonBaseResponse>) =>
        response.data,
      providesTags: [GET_TEACHER_LESSON_BASE],
    }),

    // GET /teacher/assignments/lesson-students
    getTeacherLessonStudents: build.query<
      ITeacherLessonStudentsResponse,
      ITeacherLessonStudentsRequest
    >({
      query: (params) => ({
        url: '/teacher/assignments/lesson-students',
        params,
      }),
      transformResponse: (response: SuccessApiResponse<ITeacherLessonStudentsResponse>) =>
        response.data,
      providesTags: [GET_TEACHER_LESSON_STUDENTS],
    }),

    // GET /teacher/assignments/lesson-submission
    getTeacherLessonSubmission: build.query<
      ITeacherLessonSubmissionResponse,
      ITeacherLessonSubmissionRequest
    >({
      query: (params) => ({
        url: '/teacher/assignments/lesson-submission',
        params,
      }),
      transformResponse: (response: SuccessApiResponse<ITeacherLessonSubmissionResponse>) =>
        response.data,
      providesTags: [GET_TEACHER_LESSON_SUBMISSION],
    }),

    // PUT /teacher/assignments/submission/{submissionId}/assessment
    assessSubmission: build.mutation<
      ITeacherAssessmentResponse,
      { submissionId: number; data: ITeacherAssessmentRequest }
    >({
      query: ({ submissionId, data }) => ({
        url: `/teacher/assignments/submission/${submissionId}/assessment`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: (response: SuccessApiResponse<ITeacherAssessmentResponse>) =>
        response.data,
      invalidatesTags: [
        GET_TEACHER_ASSIGNMENTS,
        GET_TEACHER_ASSIGNMENTS_SUMMARY,
        GET_TEACHER_ASSIGNMENTS_DETAILS,
        GET_TEACHER_LESSON_STUDENTS,
        GET_TEACHER_LESSON_SUBMISSION,
      ],
    }),

    // PUT /teacher/assignments/submission/{submissionId}/assessment-with-feedback
    assessSubmissionWithFeedback: build.mutation<
      ITeacherAssessmentWithFeedbackResponse,
      { submissionId: number; data: ITeacherAssessmentWithFeedbackRequest }
    >({
      query: ({ submissionId, data }) => {
        const payload: ITeacherAssessmentWithFeedbackRequest = {
          assignmentStatus: data.assignmentStatus,
          feedbackText: data.feedbackText,
        };

        if (data.grade !== undefined && data.grade !== null) {
          payload.grade = data.grade;
        }

        if (data.feedbackFiles?.length) {
          payload.feedbackFiles = data.feedbackFiles;
        }

        return {
          url: `/teacher/assignments/submission/${submissionId}/assessment-with-feedback`,
          method: 'PUT',
          body: createFormData(payload),
        };
      },
      transformResponse: (response: SuccessApiResponse<ITeacherAssessmentWithFeedbackResponse>) =>
        response.data,
      invalidatesTags: [
        GET_TEACHER_ASSIGNMENTS,
        GET_TEACHER_ASSIGNMENTS_SUMMARY,
        GET_TEACHER_ASSIGNMENTS_DETAILS,
        GET_TEACHER_LESSON_STUDENTS,
        GET_TEACHER_LESSON_SUBMISSION,
      ],
    }),
  }),
});

export const {
  useGetTeacherAssignmentsQuery,
  useGetTeacherAssignmentsSummaryQuery,
  useGetTeacherAssignmentsDetailsQuery,
  useGetTeacherAssignmentsCoursesQuery,
  useGetTeacherLessonBaseQuery,
  useGetTeacherLessonStudentsQuery,
  useGetTeacherLessonSubmissionQuery,
  useAssessSubmissionMutation,
  useAssessSubmissionWithFeedbackMutation,
} = teacherAssignmentApi;
